import { makeid } from 'utils/helpers/makeid';
import { DateFormatter } from 'infrastructure/functions';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createSleepMatOnboardingTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth. I'm calling to help
          you get started with your Remote Patient Monitoring program. For
          quality and compliance purposes, this call may be recorded. To protect
          your privacy, can you please confirm your full name and date of
          birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'onboardingPurposeReassurance',
      type: 'checkbox',
      title: <p>Onboarding Purpose & Reassurance</p>,
      description: (
        <ScriptWrapper>
          "The goal of today's call is to help you get your sleep and resting
          monitor set up, make sure everything is positioned correctly, and
          explain how it works."
          <br />
          "There's no way to do this wrong — I'll walk you through it step by
          step, and once it's set up, it works automatically."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Purpose explained',
          value: 'purposeExplained',
        },
        {
          key: makeid(10),
          label: 'Reassurance provided',
          value: 'reassuranceProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'earlyGoalSetting',
      type: 'checkbox',
      title: <p>Early Goal Setting (Clinical Best Practice)</p>,
      description: (
        <ScriptWrapper>
          "As part of your care, your clinic recommends using the sleep monitor
          every night."
          <br />
          "Using it consistently helps your care team understand your sleep and
          overnight resting patterns and notice changes early."
          <br />
          "If you miss a night, that's okay — consistency over time is what
          matters most."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Nightly use encouraged as best practice',
          value: 'nightlyUseEncouragedAsBestPractice',
        },
        {
          key: makeid(10),
          label: 'Flexibility and consistency emphasized',
          value: 'flexibilityAndConsistencyEmphasized',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'devicePackageReadiness',
      type: 'radio',
      title: <p>Device & Package Readiness Check</p>,
      description: (
        <ScriptWrapper>
          "Do you have the sleep monitor package with you right now?"
          <br />
          If NO: "That's okay — we can review how it works today, and you can
          set it up later."
          <br />
          If YES: "Great — let's go through what's in the box together."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Package present during call',
          value: 'packagePresentDuringCall',
        },
        {
          key: makeid(10),
          label: 'Package not present during call',
          value: 'packageNotPresentDuringCall',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'packageContentsReviewed',
      type: 'checkbox',
      title: <p>Package Contents Reviewed</p>,
      description: (
        <ScriptWrapper>
          "In the box, you should have the sleep and resting monitor, a white
          USB cable, a wall adapter, and a quick-start visual guide."
          <br />
          "Let me know once you see those items."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Monitor present',
          value: 'monitorPresent',
        },
        {
          key: makeid(10),
          label: 'USB cable present',
          value: 'usbCablePresent',
        },
        {
          key: makeid(10),
          label: 'Wall adapter present',
          value: 'wallAdapterPresent',
        },
        {
          key: makeid(10),
          label: 'Visual guide present',
          value: 'visualGuidePresent',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'powerChargingSetup',
      type: 'checkbox',
      title: <p>Power & Charging Setup</p>,
      description: (
        <ScriptWrapper>
          "It's important that the sleep monitor stays plugged in at all times."
          <br />
          "Please connect the white USB cable to the monitor and plug it into
          the wall adapter."
          <br />
          "Let me know once it's plugged in and powered on."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Device plugged in',
          value: 'devicePluggedIn',
        },
        {
          key: makeid(10),
          label: 'Continuous power requirement explained',
          value: 'continuousPowerRequirementExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'bedSheetSetupReviewed',
      type: 'checkbox',
      title: <p>Bed & Sheet Setup Reviewed</p>,
      description: (
        <ScriptWrapper>
          "Now let's talk about where the monitor goes."
          <br />
          "The sleep mat should be placed on your mattress, underneath your
          sheet."
          <br />
          "Please use a thin sheet rather than a thick one, as thick bedding can
          interfere with readings."
          <br />
          "Once it's placed, try to keep the mat in the same position every
          night."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Monitor placed under sheet',
          value: 'monitorPlacedUnderSheet',
        },
        {
          key: makeid(10),
          label: 'Thin sheet confirmed',
          value: 'thinSheetConfirmed',
        },
        {
          key: makeid(10),
          label: 'Consistent placement emphasized',
          value: 'consistentPlacementEmphasized',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'automaticReadingScheduleExplained',
      type: 'checkbox',
      title: <p>Automatic Reading Schedule Explained</p>,
      description: (
        <ScriptWrapper>
          "Once the sleep monitor is set up and plugged in, it works
          automatically."
          <br />
          "You don't need to press any buttons or start readings yourself."
          <br />
          "It typically takes readings overnight — usually around 8 PM, 12 AM, 4
          AM, and 8 AM."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Automatic readings explained',
          value: 'automaticReadingsExplained',
        },
        {
          key: makeid(10),
          label: 'Typical overnight times reviewed',
          value: 'typicalOvernightTimesReviewed',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'transmissionIndicatorExplained',
      type: 'radio',
      title: <p>Transmission Indicator Explained</p>,
      description: (
        <ScriptWrapper>
          "When the device is sending information, you may see a blinking blue
          light."
          <br />
          "That blinking blue light means your readings are being transmitted."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Blinking blue light explained',
          value: 'blinkingBlueLightExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'programValueReinforced',
      type: 'checkbox',
      title: <p>Program Value Reinforced</p>,
      description: (
        <ScriptWrapper>
          "The information collected overnight helps your care team understand
          your sleep and resting patterns over time."
          <br />
          "Even nights that feel 'normal' are important — they help establish
          your baseline and detect changes earlier."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Trend monitoring explained',
          value: 'trendMonitoringExplained',
        },
        {
          key: makeid(10),
          label: 'Engagement encouraged',
          value: 'engagementEncouraged',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we currently have on file for
          you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
          <br />
          If Medicare Advantage: "Which plan is listed?"
          <br />
          "Thank you — I'll confirm what's documented in your chart."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "Before we continue, I want to confirm the best cell phone number to
          reach you. Is [number on file] still correct?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone number confirmed',
          value: 'cellPhoneNumberConfirmed',
        },
        {
          key: makeid(10),
          label: 'Cell phone number updated',
          value: 'cellPhoneNumberUpdated',
        },
        {
          key: makeid(10),
          label: 'Patient reports no cell phone',
          value: 'patientReportsNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          "If you have a cell phone, we also offer text messages for reminders
          and updates related to your RPM care."
          <br />
          "Would you like to receive text messages from us?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered and accepted → Enrolled',
          value: 'offeredAndAcceptedEnrolled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — I'll get you enrolled. You can opt out at any time."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Offered and declined',
          value: 'offeredAndDeclined',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "No problem — that's completely optional."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Not eligible — no cell phone',
          value: 'notEligibleNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Next Monthly Check-In</p>,
      description: (
        <ScriptWrapper>
          "As part of the program, a monthly phone call with our team is
          required."
          <br />
          "These calls help us check in, answer questions, and make sure
          everything continues to work smoothly."
          <br />
          "If your weight changes outside of your provider's recommended range,
          your clinic may reach out — often with a call from a nurse — to decide
          if any follow-up is needed."
          <br />
          "Would you like to schedule your first monthly check-in now, or should
          we reach out closer to when it's due?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Requirement reviewed',
          value: 'requirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Value explained',
          value: 'valueExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Before we disconnect, do you have any additional questions for me
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand everything we discussed
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time today. Please feel free to reach out if you
          need additional support. Take care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TSleepMatOnboardingTemplateValues = ExtractStepValues<
  ReturnType<typeof createSleepMatOnboardingTemplate>
>;

export const createSleepMatOnboardingBlurb = (
  data: TSleepMatOnboardingTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    onboardingPurposeReassurance,
    earlyGoalSetting,
    devicePackageReadiness,
    packageContentsReviewed,
    powerChargingSetup,
    bedSheetSetupReviewed,
    automaticReadingScheduleExplained,
    transmissionIndicatorExplained,
    programValueReinforced,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    nextMonthlyCheckInScheduledDate,
    closingAdditionalQuestions,
    closingUnderstanding,
  } = data;

  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    onboardingPurposeReassurance?.length &&
    !hasField(ctx, 'onboardingPurposeReassurance')
  ) {
    addField(ctx, 'onboardingPurposeReassurance');
    const map = {
      purposeExplained: 'Onboarding call purpose explained.',
      reassuranceProvided:
        'Patient reassured regarding sleep monitor setup and automatic functionality.',
    };
    txt.push(...mapValuesToText(onboardingPurposeReassurance, map));
  }

  if (earlyGoalSetting?.length && !hasField(ctx, 'earlyGoalSetting')) {
    addField(ctx, 'earlyGoalSetting');
    const map = {
      nightlyUseEncouragedAsBestPractice:
        'Nightly sleep monitor use encouraged.',
      flexibilityAndConsistencyEmphasized:
        'Importance of consistency over time emphasized.',
    };
    txt.push(...mapValuesToText(earlyGoalSetting, map));
  }

  if (devicePackageReadiness && !hasField(ctx, 'devicePackageReadiness')) {
    addField(ctx, 'devicePackageReadiness');
    const map = {
      packagePresentDuringCall: 'present',
      packageNotPresentDuringCall: 'not present',
    };

    txt.push(
      `Sleep monitor package availability reviewed. Package ${map[devicePackageReadiness]}.`,
    );

    if (devicePackageReadiness === 'packageNotPresentDuringCall') {
      txt.push('Education provided and setup deferred.');
    }
  }

  if (
    packageContentsReviewed?.length &&
    !hasField(ctx, 'packageContentsReviewed')
  ) {
    addField(ctx, 'packageContentsReviewed');
    const map = {
      monitorPresent: 'Monitor present in package.',
      usbCablePresent: 'USB cable present.',
      wallAdapterPresent: 'Wall adapter present.',
      visualGuidePresent: 'Visual guide present.',
    };
    txt.push(...mapValuesToText(packageContentsReviewed, map));
  }

  if (powerChargingSetup?.length && !hasField(ctx, 'powerChargingSetup')) {
    addField(ctx, 'powerChargingSetup');
    const map = {
      devicePluggedIn: 'Device plugged in.',
      continuousPowerRequirementExplained:
        'Continuous power requirement explained.',
    };
    txt.push(...mapValuesToText(powerChargingSetup, map));
  }

  if (
    bedSheetSetupReviewed?.length &&
    !hasField(ctx, 'bedSheetSetupReviewed')
  ) {
    addField(ctx, 'bedSheetSetupReviewed');
    const map = {
      monitorPlacedUnderSheet: 'Monitor placement under sheet confirmed.',
      thinSheetConfirmed: 'Thin sheet confirmed.',
      consistentPlacementEmphasized: 'Consistent nightly placement emphasized.',
    };
    txt.push(...mapValuesToText(bedSheetSetupReviewed, map));
  }

  if (
    automaticReadingScheduleExplained?.length &&
    !hasField(ctx, 'automaticReadingScheduleExplained')
  ) {
    addField(ctx, 'automaticReadingScheduleExplained');
    const map = {
      automaticReadingsExplained: 'Automatic readings explained.',
      typicalOvernightTimesReviewed: 'Typical overnight times reviewed.',
    };
    txt.push(...mapValuesToText(automaticReadingScheduleExplained, map));
  }

  if (
    transmissionIndicatorExplained &&
    !hasField(ctx, 'transmissionIndicatorExplained')
  ) {
    addField(ctx, 'transmissionIndicatorExplained');
    txt.push(
      `Transmission indicator reviewed. Patient informed that blinking blue light indicates data transmission.`,
    );
  }

  if (
    programValueReinforced?.length &&
    !hasField(ctx, 'programValueReinforced')
  ) {
    addField(ctx, 'programValueReinforced');
    const map = {
      trendMonitoringExplained:
        'Program value reviewed, including baseline and trend monitoring.',
      engagementEncouraged: 'Patient engagement in monitoring encouraged.',
    };
    txt.push(...mapValuesToText(programValueReinforced, map));
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');
    const map = {
      medicare: 'Medicare',
      medicareAdvantage: 'Medicare advantage',
      commercial: 'Commercial',
    };
    txt.push(
      `Insurance type confirmed as ${map[insuranceVerificationOnFile]} based on information on file.`,
    );
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');
    const map = {
      cellPhoneNumberConfirmed: 'confirmed',
      cellPhoneNumberUpdated: 'updated',
      patientReportsNoCellPhone: 'patient reports no cell phone',
    };
    txt.push(
      `Cell phone contact verified with patient ${map[cellPhoneVerification]}.`,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');
    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAndAcceptedEnrolled: 'offered and accepted',
      offeredAndDeclined: 'offered and declined',
      notEligibleNoCellPhone: 'not eligible—no cell phone',
    };
    txt.push(`Texting enrollment reviewed ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');
    const map = {
      requirementReviewed: 'monthly phone call requirement',
      valueExplained: 'program monitoring process explained',
    };
    const items = mapValuesToText(monthlyInteractionRequirement, map);
    if (items.length) {
      txt.push(
        `Monthly interaction requirement reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');
    const map = {
      nextMonthlyCheckInScheduled: 'scheduled',
      patientDeclinedScheduling: 'not scheduled—patient declined',
    };
    let val = map[monthlyInteractionScheduling];
    if (
      monthlyInteractionScheduling === 'nextMonthlyCheckInScheduled' &&
      nextMonthlyCheckInScheduledDate
    ) {
      val += `in ${DateFormatter({
        date: nextMonthlyCheckInScheduledDate,
      })}`;
    }

    txt.push(`First monthly check-in ${val}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }

  return txt.join(' ');
};
